import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { signInWithPassword, signUp, signOut as supabaseSignOut } from '../utils/supabaseAuth';

const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      api.get('/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setUser(res.data.user))
      .catch(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
      });
    }
  }, [token]);

  const login = async (email, password) => {
    const { data, error } = await signInWithPassword(email, password);
    if (error) {
      throw new Error(error.message || 'Sign-in failed');
    }
    const accessToken = data?.session?.access_token;
    if (!accessToken) {
      throw new Error('Sign-in did not return a session');
    }
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    const profile = await api.post('/auth/session', {}, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setUser(profile.data.user);
  };

  const register = async (email, password, firstName, lastName, role = 'sales_rep') => {
    const { data, error } = await signUp(email, password, {
      first_name: firstName,
      last_name: lastName,
      role,
    });
    if (error) {
      throw new Error(error.message || 'Registration failed');
    }
    const accessToken = data?.session?.access_token;
    if (!accessToken) {
      return { needsEmailConfirmation: true };
    }
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    const profile = await api.post('/auth/session', {
      first_name: firstName,
      last_name: lastName,
      role,
    }, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    setUser(profile.data.user);
    return profile.data;
  };

  const logout = () => {
    supabaseSignOut().catch(() => {});
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}